import { useState, useRef, useEffect, useCallback } from "react";
import { useOptionColors } from "@/hooks/useOptionColors";
import { useQueryClient } from "@tanstack/react-query";
import { useSubscription } from "@/hooks/useSubscription";
import analytics from "@/lib/segment";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Upload, Download, MoreHorizontal, Camera, X, Info } from "lucide-react";
import { Expense, ExpenseFormData, useExpenses } from "@/hooks/useExpenses";
import { ExpenseTableCell } from "./ExpenseTableCell";
import { ReceiptFilesCell } from "./ReceiptFilesCell";
import { ManageOptionsDialog } from "./ManageOptionsDialog";
import { ExpenseImportDialog } from "./ExpenseImportDialog";
import { ClearExpensesDialog } from "./ClearExpensesDialog";
import { UploadReceiptsDialog, ParsedReceiptExpense } from "./UploadReceiptsDialog";
import { exportExpensesToCSV } from "@/lib/exportExpenses";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const ExpenseTable = ({ highlightVehicleId }: { highlightVehicleId?: string | null }) => {
  const queryClient = useQueryClient();
  const {
    expenses,
    isLoading,
    vehicles,
    expenseTypes,
    paidByOptions,
    taxCategories,
    addExpense,
    updateExpense,
    deleteExpense,
    addExpenseType,
    addPaidByOption,
    addTaxCategory,
    importExpenses,
    clearAllExpenses,
  } = useExpenses();
  const { vehicleLimit } = useSubscription();
  const activeColorValues = {
    vehicle: ["operations", ...vehicles.map((v) => v.id)],
    expense_type: expenseTypes,
    paid_by: paidByOptions,
  };
  const { getColor, setColor } = useOptionColors(activeColorValues);
  const { company } = useCompany();

  const [editingCell, setEditingCell] = useState<{
    expenseId: string;
    field: string;
  } | null>(null);

  const [manageOptionsDialog, setManageOptionsDialog] = useState<{
    isOpen: boolean;
    type: "expense_type" | "paid_by" | "tax_category";
  } | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [highlightedExpenseIds, setHighlightedExpenseIds] = useState<Set<string>>(new Set());
  const [errorExpenseIds, setErrorExpenseIds] = useState<Set<string>>(new Set());
  const [showReceiptBanner, setShowReceiptBanner] = useState(false);

  const handleExport = () => {
    if (expenses.length === 0) {
      toast.error("No expenses to export");
      return;
    }
    exportExpensesToCSV(expenses, vehicles);
    toast.success("Expenses exported to CSV");
  };

  const handleImport = async (file: File) => {
    return await importExpenses.mutateAsync({ file, vehicleLimit });
  };

  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [recentlyMovedExpenseIds, setRecentlyMovedExpenseIds] = useState<Set<string>>(new Set());
  const [lastUsedDate, setLastUsedDate] = useState<string | null>(null);
  const [lastUsedPaidBy, setLastUsedPaidBy] = useState<string | null>(null);
  const [scrollRequest, setScrollRequest] = useState<{ id: string; nonce: number } | null>(null);
  const [pendingDateScroll, setPendingDateScroll] = useState<{
    id: string;
    expenseDate: string;
    previousIndex: number;
  } | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const setRowRef = useCallback((id: string, el: HTMLTableRowElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  const requestScrollTo = useCallback((id: string) => {
    setScrollRequest({ id, nonce: Date.now() });
  }, []);

  const triggerTemporaryHighlight = useCallback((id: string) => {
    setRecentlyMovedExpenseIds((prev) => new Set(prev).add(id));
    window.setTimeout(() => {
      setRecentlyMovedExpenseIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 60000);
  }, []);

  const scrollRequestRef = useRef(scrollRequest);
  scrollRequestRef.current = scrollRequest;

  useEffect(() => {
    if (!scrollRequest) return;

    const currentNonce = scrollRequest.nonce;
    let attempts = 0;

    const attemptScroll = () => {
      // If a newer request replaced this one, stop
      if (scrollRequestRef.current?.nonce !== currentNonce) return;

      const el = rowRefs.current.get(scrollRequest.id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setScrollRequest(null);
        return;
      }

      if (attempts >= 30) {
        setScrollRequest(null);
        return;
      }

      attempts += 1;
      window.setTimeout(attemptScroll, 150);
    };

    window.setTimeout(attemptScroll, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRequest?.nonce]);

  useEffect(() => {
    if (!pendingDateScroll) return;

    const currentIndex = expenses.findIndex((expense) => expense.id === pendingDateScroll.id);
    if (currentIndex === -1) return;

    const currentExpense = expenses[currentIndex];
    if (currentExpense.expense_date !== pendingDateScroll.expenseDate) return;

    if (currentIndex !== pendingDateScroll.previousIndex) {
      requestScrollTo(pendingDateScroll.id);
    }

    setPendingDateScroll(null);
  }, [expenses, pendingDateScroll, requestScrollTo]);

  const handleAddRow = () => {
    const defaultDate = lastUsedDate || new Date().toISOString().split("T")[0];
    addExpense.mutate(
      {
        expense_date: defaultDate,
        expense_name: "",
        amount: 0,
        paid_by: lastUsedPaidBy || undefined,
      },
      {
        onSuccess: (data) => {
          setNewRowId((prev) => {
            if (prev) {
              setRecentlyMovedExpenseIds((s) => { const ns = new Set(s); ns.delete(prev); return ns; });
            }
            return data.id;
          });
          requestScrollTo(data.id);
          window.setTimeout(() => setNewRowId((current) => (current === data.id ? null : current)), 60000);
        },
      }
    );
  };

  const handleCellUpdate = (
    expenseId: string,
    field: keyof ExpenseFormData,
    value: string | number | null
  ) => {
    const previousIndex = expenses.findIndex((expense) => expense.id === expenseId);

    updateExpense.mutate(
      { id: expenseId, [field]: value },
      {
        onSuccess: () => {
          if (field === "expense_date" && typeof value === "string" && value) {
            setPendingDateScroll({
              id: expenseId,
              expenseDate: value,
              previousIndex,
            });
          }
        },
      }
    );

    if (field === "expense_date" && typeof value === "string" && value) {
      setLastUsedDate(value);
      triggerTemporaryHighlight(expenseId);
    }
    if (field === "paid_by" && typeof value === "string") {
      setLastUsedPaidBy(value || null);
    }
    setEditingCell(null);
  };

  const handleReceiptUrlsUpdate = (expenseId: string, urls: string[]) => {
    updateExpense.mutate({ id: expenseId, receipt_urls: urls } as any);
  };

  const handleDeleteRow = (id: string) => {
    const expense = expenses.find((e) => e.id === id);
    const name = expense?.expense_name?.trim() || "this expense";
    if (confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteExpense.mutate(id);
    }
  };

  const handleReceiptsParsed = async (
    parsedExpenses: ParsedReceiptExpense[],
    receiptUrls: string[]
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const successIds = new Set<string>();
      const failedIds = new Set<string>();
      let dbErrors = 0;

      for (let i = 0; i < parsedExpenses.length; i++) {
        const exp = parsedExpenses[i];
        const { _receipt_url, _parse_failed, ...rest } = exp;
        const row = {
          expense_date: rest.expense_date || new Date().toISOString().split("T")[0],
          expense_name: rest.expense_name || "",
          amount: rest.amount || 0,
          expense_type: rest.expense_type || null,
          vehicle_id: rest.vehicle_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rest.vehicle_id) ? rest.vehicle_id : null,
          paid_by: rest.paid_by || null,
          paid_to: rest.paid_to || null,
          notes: rest.notes || null,
          mileage: rest.mileage || null,
          receipt_urls: _receipt_url ? [_receipt_url] : [],
          user_id: user.id,
          company_id: company?.id || null,
        };

        const { data: inserted, error } = await supabase
          .from("expenses")
          .insert(row)
          .select()
          .single();

        if (error) {
          console.error(`Failed to insert expense ${i + 1}:`, error.message);
          dbErrors++;
          continue;
        }

        if (inserted) {
          if (_parse_failed) {
            failedIds.add(inserted.id);
          } else {
            successIds.add(inserted.id);
          }
        }
      }

      if (successIds.size + failedIds.size === 0) {
        toast.error("Failed to save any expenses");
        return;
      }

      setHighlightedExpenseIds(successIds);
      setErrorExpenseIds(failedIds);
      setShowReceiptBanner(true);

      setTimeout(() => {
        setHighlightedExpenseIds(new Set());
        setErrorExpenseIds(new Set());
        setShowReceiptBanner(false);
      }, 240000);

      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });

      analytics.track("Receipts Scanned", {
        receipt_count: receiptUrls.length,
        expense_count: successIds.size + failedIds.size,
        parse_error_count: failedIds.size,
        db_error_count: dbErrors,
      });

      if (dbErrors > 0) {
        toast.warning(`Saved ${successIds.size + failedIds.size} expense(s), but ${dbErrors} failed to save.`);
      }
    } catch (err: any) {
      toast.error("Failed to save expenses: " + err.message);
    }
  };

  const vehicleOptions = [
    { value: "operations", label: "Operations" },
    ...vehicles.map((v) => ({
      value: v.id,
      label: v.name || `${v.year} ${v.make} ${v.model}`,
    })),
  ];

  const expenseTypeOptions = expenseTypes.map((t) => ({
    value: t,
    label: t,
  }));

  const paidByOptionsList = paidByOptions.map((p) => ({
    value: p,
    label: p,
  }));

  const taxCategoryOptions = taxCategories.map((t) => ({
    value: t,
    label: t,
  }));

  const columns = [
    { key: "expense_date", label: "Date", type: "date" as const },
    { key: "expense_name", label: "Expense", type: "text" as const },
    {
      key: "vehicle_id",
      label: "Car",
      type: "select" as const,
      options: vehicleOptions,
    },
    {
      key: "expense_type",
      label: "Expense Type",
      type: "select" as const,
      options: expenseTypeOptions,
      onManage: () => setManageOptionsDialog({ isOpen: true, type: "expense_type" }),
    },
    {
      key: "tax_category",
      label: "Tax Category",
      type: "select" as const,
      options: taxCategoryOptions,
      onManage: () => setManageOptionsDialog({ isOpen: true, type: "tax_category" }),
    },
    { key: "amount", label: "Amount", type: "currency" as const },
    { key: "mileage", label: "Miles Driven", type: "number" as const },
    {
      key: "paid_by",
      label: "Paid By",
      type: "select" as const,
      options: paidByOptionsList,
      onManage: () => setManageOptionsDialog({ isOpen: true, type: "paid_by" }),
    },
    { key: "paid_to", label: "Paid To", type: "text" as const },
    { key: "notes", label: "Notes", type: "text" as const },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="sticky top-[72px] md:top-14 z-20 bg-background py-2 -my-2 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-lg font-semibold">Expenses</h2>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => setImportDialogOpen(true)} variant="outline" size="sm">
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => setReceiptDialogOpen(true)} variant="outline" size="sm">
              <Camera className="h-4 w-4 mr-2" />
              Upload Receipts
            </Button>
            <Button onClick={handleAddRow} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            {expenses.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExport}>
                    <Download className="h-4 w-4 mr-2" />
                    Export All Expenses
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setClearDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All Expenses
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {showReceiptBanner && (
          <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${
            errorExpenseIds.size > 0
              ? "border-destructive/30 bg-destructive/10"
              : "border-primary/30 bg-primary/10"
          }`}>
            <Info className={`h-4 w-4 shrink-0 ${errorExpenseIds.size > 0 ? "text-destructive" : "text-primary"}`} />
            <span className="flex-1">
              {errorExpenseIds.size > 0 ? (
                <>
                  <strong>{highlightedExpenseIds.size + errorExpenseIds.size} expense{(highlightedExpenseIds.size + errorExpenseIds.size) !== 1 ? "s" : ""}</strong> created from receipts.
                  {" "}<span className="text-destructive font-medium">{errorExpenseIds.size} expense{errorExpenseIds.size !== 1 ? "s" : ""} highlighted in red</span> could not be parsed — please fill in the details manually.
                  {highlightedExpenseIds.size > 0 && <> The rest are highlighted in blue for review.</>}
                </>
              ) : (
                <>
                  <strong>{highlightedExpenseIds.size} expense{highlightedExpenseIds.size !== 1 ? "s" : ""}</strong> created from receipts and highlighted in blue. Please review them for accuracy and make any corrections needed.
                </>
              )}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => {
                setShowReceiptBanner(false);
                setHighlightedExpenseIds(new Set());
                setErrorExpenseIds(new Set());
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        <div className="border rounded-lg overflow-x-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-muted/50">
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={`font-semibold text-xs ${
                      col.key === "expense_date" ? "w-[130px]" :
                      col.key === "expense_name" ? "w-[140px]" :
                      col.key === "vehicle_id" ? "w-[140px]" :
                      col.key === "expense_type" ? "w-[170px]" :
                      col.key === "tax_category" ? "w-[180px]" :
                      col.key === "amount" ? "w-[100px]" :
                      col.key === "mileage" ? "w-[80px]" :
                      col.key === "paid_by" ? "w-[110px]" :
                      col.key === "paid_to" ? "w-[110px]" :
                      col.key === "notes" ? "w-[140px]" :
                      ""
                    }`}
                  >
                    {col.label}
                  </TableHead>
                ))}
                <TableHead className="w-[80px] font-semibold text-xs">Receipts</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 2}
                    className="text-center text-muted-foreground py-8"
                  >
                    <div className="space-y-2">
                      <p className="text-sm">No expenses yet. Click "Add Row" to create your first expense.</p>
                      <p className="text-sm">
                        Or{" "}
                        <a
                          href="/samples/expenses-sample.csv"
                          download="expenses-sample.csv"
                          className="text-primary hover:underline font-medium"
                        >
                          download a sample CSV
                        </a>{" "}
                        to try the import feature.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => (
                  <TableRow
                    key={expense.id}
                    ref={(el) => setRowRef(expense.id, el)}
                    className={`group ${
                      errorExpenseIds.has(expense.id)
                        ? "bg-destructive/10 hover:bg-destructive/10 border-l-2 border-l-destructive animate-in fade-in duration-500"
                        : highlightedExpenseIds.has(expense.id)
                        ? "bg-primary/10 hover:bg-primary/15 border-l-2 border-l-primary animate-in fade-in duration-500"
                        : newRowId === expense.id || recentlyMovedExpenseIds.has(expense.id)
                        ? "bg-primary/10 hover:bg-primary/15 border-l-2 border-l-primary animate-in fade-in duration-500"
                        : highlightVehicleId && (
                            (highlightVehicleId === "operations" && !expense.vehicle_id) ||
                            (highlightVehicleId !== "operations" && expense.vehicle_id === highlightVehicleId)
                          )
                        ? "bg-green-500/10 hover:bg-green-500/15 border-l-2 border-l-green-500"
                        : ""
                    }`}
                  >
                    {columns.map((col) => {
                      const tabbableFields = ["expense_name", "amount", "mileage", "paid_to", "notes"];
                      const tabIndex = tabbableFields.indexOf(col.key);
                      return (
                      <ExpenseTableCell
                        key={col.key}
                        expense={expense}
                        field={col.key}
                        type={col.type}
                        options={col.options}
                        isEditing={
                          editingCell?.expenseId === expense.id &&
                          editingCell?.field === col.key
                        }
                        onStartEdit={() =>
                          setEditingCell({ expenseId: expense.id, field: col.key })
                        }
                        onUpdate={(value) =>
                          handleCellUpdate(expense.id, col.key as keyof ExpenseFormData, value)
                        }
                        onCancel={() => setEditingCell(null)}
                        onManage={col.onManage}
                        vehicles={vehicles}
                        getColor={getColor}
                        onColorChange={(type, value, color) => setColor.mutate({ type, value, color })}
                        onTabNext={tabIndex >= 0 ? () => {
                          const nextField = tabbableFields[(tabIndex + 1) % tabbableFields.length];
                          setEditingCell({ expenseId: expense.id, field: nextField });
                        } : undefined}
                        onTabPrev={tabIndex >= 0 ? () => {
                          const prevField = tabbableFields[(tabIndex - 1 + tabbableFields.length) % tabbableFields.length];
                          setEditingCell({ expenseId: expense.id, field: prevField });
                        } : undefined}
                      />
                      );
                    })}
                    <ReceiptFilesCell
                      expenseId={expense.id}
                      receiptUrls={(expense.receipt_urls as string[]) || []}
                      onUpdate={(urls) => handleReceiptUrlsUpdate(expense.id, urls)}
                    />
                    <TableCell className="p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                        onClick={() => handleDeleteRow(expense.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {manageOptionsDialog && (
        <ManageOptionsDialog
          isOpen={manageOptionsDialog.isOpen}
          onClose={() => setManageOptionsDialog(null)}
          type={manageOptionsDialog.type}
          options={
            manageOptionsDialog.type === "expense_type"
              ? expenseTypes
              : manageOptionsDialog.type === "tax_category"
              ? taxCategories
              : paidByOptions
          }
          onAdd={(value) =>
            manageOptionsDialog.type === "expense_type"
              ? addExpenseType.mutate(value)
              : manageOptionsDialog.type === "tax_category"
              ? addTaxCategory.mutate(value)
              : addPaidByOption.mutate(value)
          }
        />
      )}

      <ExpenseImportDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
      />

      <ClearExpensesDialog
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        onConfirm={() => {
          analytics.track("Expenses Cleared", {
            expense_count: expenses.length,
          });
          clearAllExpenses.mutate();
          setClearDialogOpen(false);
        }}
        onExport={() => {
          handleExport();
          setClearDialogOpen(false);
        }}
        isLoading={clearAllExpenses.isPending}
        expenseCount={expenses.length}
      />

      <UploadReceiptsDialog
        isOpen={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        onExpensesParsed={handleReceiptsParsed}
        vehicles={vehicles}
        expenseTypes={expenseTypes}
        paidByOptions={paidByOptions}
      />
    </>
  );
};
